import React, { useEffect, useState } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { callSheetDateLabel, callSheetName, fetchCallSheet, type CallSheetPayload } from '../../utils/callSheets';
import type { ShootDayContact } from '../../utils/shootDaySource';

/**
 * A call sheet, to READ, on a phone (24 Sep 2026).
 *
 * The builder is a form, and a form is the wrong thing to hand someone at
 * 6am in a car park. On a shoot day the questions are: what time, where is
 * it, and who do I ring — in that order. So this screen answers them in that
 * order and nothing else competes.
 *
 * Two things make it worth the file rather than a narrower builder:
 *
 *   • EVERY NUMBER DIALS. A phone number printed as text on a phone is a
 *     number you have to memorise and retype. Each person is one `tel:` link
 *     across the whole row, so it works with a thumb and without looking.
 *   • THE ADDRESS OPENS. Either the pasted maps link or, failing that, a
 *     search built from the address text — the crew should never be
 *     copy-pasting a venue into another app while driving.
 *
 * Editing is still the builder, behind the Edit button: filling a call sheet
 * in is desk work and pretending otherwise would produce a worse call sheet.
 * On anything wider than a phone this screen never renders at all; the
 * builder opens directly, as it always did. See utils/isPhone.ts.
 */

/** `tel:` wants digits and a leading +; anything else is decoration. */
function dialable(phone: string | null | undefined): string {
  if (!phone) return '';
  const cleaned = phone.replace(/[^\d+]/g, '');
  return cleaned.length >= 6 ? cleaned : '';
}

/** The pasted link if there is one, else a search for the address text. */
function mapsHref(address: string, pasted: string): string {
  if (pasted.trim()) return pasted.trim();
  if (!address.trim()) return '';
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.trim())}`;
}
const SECTION = 'border-t border-ruleDark pt-5';
const HEADING = 'label m-0 text-paper/60';
const LINK = 'inline-flex h-11 items-center text-paper transition-opacity duration-150 ease-out hover:opacity-70';
const LINK_TEXT = 'label border-b border-paper/45 pb-1';

/**
 * One person. The whole row is the call target when there is a number, so
 * the tap area is the row rather than the eleven characters of the number.
 */
function Person({
  name,
  role,
  phone,
  email,
  remote
}: {
  name: string;
  role: string;
  phone: string;
  email: string;
  remote?: boolean;
}) {
  const tel = dialable(phone);
  const detail = [role, remote ? 'Remote' : ''].filter(Boolean).join('  ·  ');
  const body = <>
      <span className="block text-[1.0625rem] leading-snug text-paper" data-id="element-718" data-mp="cjnsooocq3hl">{name}</span>
      <span className="mt-1 block text-[0.9375rem] leading-snug text-paper/60" data-id="element-719" data-mp="7ktbts9y2p04">
        {detail}
        {detail && (phone || email) ? '  ·  ' : ''}
        {phone ? <span className="border-b border-paper/45" data-id="element-720" data-mp="hhxpt4tmze5u">{phone}</span> : email}
      </span>
    </>;
  if (!tel) {
    return <li className="border-b border-paper/10 py-3.5" data-id="element-721" data-mp="105q2flejx99r">{body}</li>;
  }
  return <li className="border-b border-paper/10" data-id="element-722" data-mp="1538dbhc4pyke">
      <a href={`tel:${tel}`} className="block py-3.5" data-id="element-723" data-mp="1526yd1rzsrxe">
        {body}
      </a>
    </li>;
}
function People({
  title,
  rows
}: {
  title: string;
  rows: ShootDayContact[];
}) {
  if (rows.length === 0) return null;
  return <section className={`${SECTION} mt-8`} data-id="element-724" data-mp="heyxqx5ie71p">
      <h2 className={HEADING} data-id="element-725" data-mp="hdxbrxmyg057">{title}</h2>
      <ul className="m-0 mt-3 list-none p-0" data-id="element-726" data-mp="m5f9mctolkly">
        {rows.map((person, index) => <Person key={person.id ?? `${person.name}-${index}`} name={person.name} role={person.role} phone={person.phone} email={person.email} remote={person.remote} data-id="element-727" data-mp="m79whwoijmso" data-mpi={index} />)}
      </ul>
    </section>;
}
export function CallSheetRead({
  callSheetId,
  onBack,
  onEdit
}: {
  callSheetId: string;
  onBack: () => void;
  onEdit: () => void;
}) {
  const [sheet, setSheet] = useState<CallSheetPayload | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let live = true;
    setLoading(true);
    void fetchCallSheet(callSheetId).then(payload => {
      if (!live) return;
      setSheet(payload);
      setLoading(false);
    });
    return () => {
      live = false;
    };
  }, [callSheetId]);
  if (loading) {
    return <p className="label text-paper/60" data-id="element-728" data-mp="n2dravzonwhb">Loading…</p>;
  }
  if (!sheet) {
    return <div data-id="element-729" data-mp="i314uccq4rf9">
        <button type="button" onClick={onBack} className="label inline-flex h-11 items-center gap-3" data-id="element-730" data-mp="i29izt8xi826">
          <ArrowLeftIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-731" data-mp="e0e7jvxaaeao" />
          Call Sheets
        </button>
        <p className="mt-6 font-display text-xl text-paper/60" data-id="element-732" data-mp="92urcqh0zrg3">That call sheet could not be opened.</p>
      </div>;
  }
  const address = sheet.locationAddress ?? '';
  const maps = mapsHref(address, sheet.locationMapUrl ?? '');
  const callTime = sheet.schedule[0]?.timeLabel?.trim() ?? '';

  /* Account crew and typed-in freelancers are one list here. The difference
     between them matters when you are building the sheet and not at all when
     you are looking for somebody's number. */
  const crew: ShootDayContact[] = [...sheet.crew.map(member => ({
    id: member.userId,
    name: member.name,
    role: member.role ?? '',
    phone: member.phone ?? '',
    email: ''
  })), ...sheet.extraCrew];

  /* The attached project's own client contact belongs with the client
     contacts typed onto this sheet, not in a section of its own. */
  const clientContacts: ShootDayContact[] = [...(sheet.clientContactName ? [{
    id: 'project-contact',
    name: sheet.clientContactName,
    role: sheet.clientName,
    phone: sheet.clientContactPhone ?? '',
    email: ''
  }] : []), ...sheet.clientContacts];
  const files = [{
    label: 'Script',
    url: sheet.scriptUrl
  }, {
    label: 'Guest bios',
    url: sheet.guestBiosUrl
  }, {
    label: 'Storyboard',
    url: sheet.storyboardUrl
  }, {
    label: 'Dropbox',
    url: sheet.dropboxUrl
  }].filter(file => Boolean(file.url?.trim()));
  return <div data-id="element-733" data-mp="1raca13j5tt8t">
      <div className="-mt-2 flex items-center justify-between" data-id="element-734" data-mp="1rb3ubi7b44js">
        <button type="button" onClick={onBack} className="label -ml-1 inline-flex h-11 items-center gap-3 px-1 text-paper/60" data-id="element-735" data-mp="1mcaq6gdwtpwa">
          <ArrowLeftIcon className="h-3.5 w-3.5" strokeWidth={1.5} data-id="element-736" data-mp="ynhnm12s8jsp" />
          Call Sheets
        </button>
        <button type="button" onClick={onEdit} className="label -mr-1 inline-flex h-11 items-center px-1 text-paper/60" data-id="element-737" data-mp="un7l4ye78bil">
          Edit
        </button>
      </div>

      <p className="label mt-4 text-paper/60" data-id="element-738" data-mp="13lt24p40vih9">{sheet.clientName}</p>
      <h1 className="mt-2 font-display text-[1.875rem] font-light leading-[1.05]" data-id="element-739" data-mp="fwgrdkt1r2cu">
        {callSheetName(sheet)}
      </h1>
      <p className="mt-2 text-[1.0625rem] leading-snug text-paper/60" data-id="element-740" data-mp="ay5j3eiegzwq">
        {callSheetDateLabel(sheet.shootDate)}
      </p>

      {/* What time, then where. Everything else on a shoot day waits. */}
      <section className={`${SECTION} mt-7`} data-id="element-741" data-mp="2rbpcg2nat277">
        <h2 className={HEADING} data-id="element-742" data-mp="2rdk21u9ilihm">Call</h2>
        <p className="m-0 mt-2 font-display text-2xl font-light leading-tight" data-id="element-743" data-mp="2dkkklmq69e7f">
          {callTime || 'Not set yet'}
        </p>
      </section>

      <section className={`${SECTION} mt-8`} data-id="element-744" data-mp="2ijdnkl7u1mtc">
        <h2 className={HEADING} data-id="element-745" data-mp="3686pqzf1sqwe">Location</h2>
        {address ? <p className="m-0 mt-2 text-[1.0625rem] leading-snug" data-id="element-746" data-mp="366vl6xds3f48">{address}</p> : <p className="m-0 mt-2 text-[1.0625rem] leading-snug text-paper/60" data-id="element-747" data-mp="3a87djao1eeg4">No address yet</p>}
        {sheet.parkingNotes?.trim() && <p className="m-0 mt-1.5 text-[1.0625rem] leading-snug text-paper/60" data-id="element-748" data-mp="318u42hzwxpve">
            {sheet.parkingNotes}
          </p>}
        {maps && <a href={maps} target="_blank" rel="noopener noreferrer" className={LINK} data-id="element-749" data-mp="1kxq410wh6yz8">
            <span className={LINK_TEXT} data-id="element-750" data-mp="1pvrjlbup0g5y">Open in Maps</span>
          </a>}
      </section>

      {sheet.schedule.length > 0 && <section className={`${SECTION} mt-8`} data-id="element-751" data-mp="2cj7zqxx3gi7d">
          <h2 className={HEADING} data-id="element-752" data-mp="2chdgdo1h0y5e">Schedule</h2>
          <ul className="m-0 mt-3 list-none p-0" data-id="element-753" data-mp="27jllqm9fyxxi">
            {sheet.schedule.map((item, index) => <li key={item.id ?? index} className="border-b border-paper/10 py-3.5" data-id="element-754" data-mp="22l0omvphkuda" data-mpi={index}>
                <span className="block text-[1.0625rem] leading-snug text-paper/60" data-id="element-755" data-mp="308j2um1x2ooo">
                  {[item.timeLabel, item.endTimeLabel].filter(Boolean).join(' – ') || '—'}
                </span>
                <span className="mt-1 block text-[1.0625rem] leading-snug" data-id="element-756" data-mp="2qipt7qlc4jn2">{item.title}</span>
                {item.locationNote?.trim() && <span className="mt-1 block text-[0.9375rem] leading-snug text-paper/60" data-id="element-757" data-mp="2liv2po5h1w2j">
                    {item.locationNote}
                  </span>}
              </li>)}
          </ul>
        </section>}

      <People title="Crew" rows={crew} data-id="element-758" data-mp="3e6h914633et3" />
      <People title="Featured guests" rows={sheet.guests} data-id="element-759" data-mp="3mhzo2c5xd85g" />
      <People title="Client" rows={clientContacts} data-id="element-760" data-mp="3qijk1ceri5q0" />

      {sheet.importantInstructions?.trim() && <section className={`${SECTION} mt-8`} data-id="element-761" data-mp="3vhmfc54xwxks">
          <h2 className={HEADING} data-id="element-762" data-mp="3vguwluwfyubx">Notes</h2>
          <p className="m-0 mt-2 whitespace-pre-line text-[1.0625rem] leading-relaxed" data-id="element-763" data-mp="4bbjqoip8p0u">
            {sheet.importantInstructions}
          </p>
        </section>}

      {sheet.sections.files && files.length > 0 && <section className={`${SECTION} mt-8`} data-id="element-764" data-mp="dbhrdtrpuus3">
          <h2 className={HEADING} data-id="element-765" data-mp="da6so8d4od7u">Files</h2>
          <div className="mt-1 flex flex-col items-start" data-id="element-766" data-mp="i27ypcjpd7pf">
            {files.map(file => <a key={file.label} href={file.url as string} target="_blank" rel="noopener noreferrer" className={LINK} data-id="element-767" data-mp="n1jfumnzef7j">
                <span className={LINK_TEXT} data-id="element-768" data-mp="wz7gft89qgxm">{file.label}</span>
              </a>)}
          </div>
        </section>}

      <div className="mt-8 border-t border-ruleDark" data-id="element-769" data-mp="2zt6nhcxlux6c" />
    </div>;
}